import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Groups as GroupsIcon,
  Article as ArticleIcon,
  Psychology as PsychologyIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { activateAI } from 'shared/utils/proposalApi';

const FEATURE_LIST = [
  {
    icon: <GroupsIcon color="primary" />,
    primary: 'Team-aware writing',
    secondary:
      "Reads every member's bio, skills, and research interests to write text that reflects your consortium's actual expertise.",
  },
  {
    icon: <ArticleIcon color="primary" />,
    primary: 'Publication grounding',
    secondary:
      'References member publications and their abstracts so every section is grounded in real prior work — no hallucinated citations.',
  },
  {
    icon: <PsychologyIcon color="primary" />,
    primary: 'Call-aligned content',
    secondary:
      'Uses the full funding call description to align tone, priorities, and evaluation criteria automatically.',
  },
  {
    icon: <AutoAwesomeIcon color="primary" />,
    primary: 'Iterative refinement',
    secondary:
      'Generate, review, and regenerate. Accept the output directly into the editor or keep it as inspiration.',
  },
];

const AIActivationDialog = ({ open, onClose, proposalId, onActivated }) => {
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);

  const handleActivate = async () => {
    setActivating(true);
    setError(null);
    const result = await activateAI(proposalId);
    if (result.success) {
      onActivated?.();
      onClose();
    } else {
      setError('Failed to activate AI features. Please try again.');
    }
    setActivating(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h6" lineHeight={1.2}>AI Section Writing</Typography>
            <Typography variant="caption" color="text.secondary">
              Powered by AI with your team's data — bios, skills, publications, and the funding call — to draft proposal sections that truly reflect your consortium's strengths and the funder's priorities.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enable AI-assisted writing for this proposal. The AI will read your
          consortium's member profiles, skills, publications, and the funding
          call to help draft compelling section content.
        </Typography>

        <List dense disablePadding sx={{ mb: 2 }}>
          {FEATURE_LIST.map((f, i) => (
            <ListItem key={i} alignItems="flex-start" disableGutters sx={{ py: 0.75 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>{f.icon}</ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>{f.primary}</Typography>}
                secondary={<Typography variant="caption" color="text.secondary">{f.secondary}</Typography>}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            bgcolor: 'warning.50',
            border: '1px solid',
            borderColor: 'warning.200',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <CreditCardIcon sx={{ color: 'warning.dark', mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={600} color="warning.dark">
                Usage-based pricing
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Each generation consumes AI tokens billed to the platform account.
                Costs are tracked per proposal. By activating, you agree that AI
                usage for this proposal will be counted against your organisation's
                quota.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
          <Chip label="AI-powered" size="small" variant="outlined" />
          <Chip label="Proposal-scoped" size="small" variant="outlined" />
          <Chip label="Member data only" size="small" variant="outlined" color="success" />
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={activating}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleActivate}
          disabled={activating}
          startIcon={
            activating
              ? <CircularProgress size={18} color="inherit" />
              : <CheckCircleIcon />
          }
          sx={{ minWidth: 160 }}
        >
          {activating ? 'Activating…' : 'Accept & Enable AI'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIActivationDialog;
